/**
 * <kod-ingredients-reveal> — animation de la composition d'ingredients KOD.
 *
 * Declenchement base sur une bande de reveal centrale, dont le bord haut
 * depend de la direction de scroll :
 * - SLIDE IN : toujours pilote par la position. Une image apparait des qu'elle
 *   entre dans la bande (par le bas), quel que soit le sens de scroll. Aucune
 *   image plus basse que la vue initiale n'est donc jamais ratee.
 * - SLIDE OUT : une image sort quand elle quitte la bande. Vers le bas, le bord
 *   haut de la bande est repousse (marge de sortie plus grande) : une image
 *   deja apparue qui remonte vers le haut de l'ecran sort donc plus tot.
 *   Vers le haut, la bande reprend sa taille normale et la position rejoue le
 *   slide in.
 */
class KodIngredientsReveal extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.kod-ingredient'));
    if (this.items.length === 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.items.forEach((item) => item.classList.add('is-in'));
      return;
    }

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    // Marge normale de la bande (entree par le bas, sortie par le haut au scroll up).
    this.margin = clamp(Number(this.dataset.margin) || 30, 0, 45);
    // Ligne de sortie haute quand on defile vers le bas. Elle doit rester ENTRE
    // `margin` (sortie tres douce) et le bord d'entree (100 - margin).
    // Plus la valeur est BASSE (proche de `margin`), plus les images persistent
    // longtemps avant de slider out.
    const entryLine = 100 - this.margin;
    this.downExitMargin = clamp(
      Number(this.dataset.scrollExitMargin) || this.margin + 4,
      this.margin,
      entryLine - 10
    );
    const startDelay = Math.max(Number(this.dataset.startDelay) || 0, 0);

    // Etat initial : bande normale, pour que le reveal d'arrivee joue proprement.
    this.scrollDirection = 'up';
    this.lastScrollY = window.scrollY;
    this.ticking = false;

    this.handleScroll = () => {
      const y = window.scrollY;
      if (y !== this.lastScrollY) {
        this.scrollDirection = y > this.lastScrollY ? 'down' : 'up';
        this.lastScrollY = y;
      }
      this.requestUpdate();
    };
    this.handleResize = () => this.requestUpdate();

    // L'etat masque (opacite 0 + decalage) est deja actif des le parsing.
    // On force un reflow pour figer cet etat comme point de depart de la
    // transition AVANT d'ajouter `is-in`, afin que les images deja dans la
    // bande au chargement animent leur entree au lieu d'apparaitre en place.
    this.items.forEach((item) => item.getBoundingClientRect());

    // Retarde le premier cycle pour laisser respirer le chargement de la page.
    window.setTimeout(() => {
      if (!this.isConnected) return;
      this.update();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
    }, startDelay);
  }

  requestUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  update() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const enterBottom = viewportHeight * (1 - this.margin / 100);
    const topMargin = this.scrollDirection === 'down' ? this.downExitMargin : this.margin;
    const exitTop = viewportHeight * (topMargin / 100);

    // Lecture des positions d'abord, puis ecriture des classes, pour eviter
    // le layout thrashing.
    const states = this.items.map((item) => {
      const rect = item.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      return center < enterBottom && center > exitTop;
    });
    this.items.forEach((item, index) => item.classList.toggle('is-in', states[index]));
  }

  disconnectedCallback() {
    if (this.handleScroll) window.removeEventListener('scroll', this.handleScroll);
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
  }
}

if (!customElements.get('kod-ingredients-reveal')) {
  customElements.define('kod-ingredients-reveal', KodIngredientsReveal);
}
